/**
 * File system types for Papan
 *
 * Tree-based: files identified by ID, parent_id for hierarchy.
 * No full path in file records — path is computed when needed.
 */

export type FileType =
  // Spreadsheets
  | 'csv'
  | 'tsv'
  | 'xlsx'
  | 'ods'
  | 'parquet'
  // Documents
  | 'docx'
  | 'doc'
  | 'odt'
  | 'rtf'
  | 'pdf'
  // Presentations
  | 'pptx'
  | 'ppt'
  | 'odp'
  // Code / markup
  | 'json'
  | 'md'
  | 'html'
  | 'xml'
  | 'yaml'
  // Text
  | 'txt'
  | 'log'
  | 'ppn'       // Dashboard
  // Media
  | 'png'
  | 'jpg'
  | 'gif'
  | 'webp'
  | 'svg'
  | 'mp4'
  | 'webm'
  | 'mov'
  // Archive
  | 'zip'
  // Database
  | 'db'
  // Special
  | 'sqlparz'   // Company data reference
  | 'folder'
  | 'unknown'

export interface FileNode {
  id: string
  parentId: string | null          // NULL = root (/papan/)
  name: string                     // Just the filename, no path
  isFolder: boolean
  contentHash: string | null       // SHA-256, NULL for folders
  size: number
  mimeType: string | null
  createdAt: Date
  updatedAt: Date
}

export interface FileContent {
  node: FileNode
  data: Uint8Array | string
  encoding: 'utf-8' | 'base64' | 'binary'
}

/**
 * Supported file types and their capabilities
 */
export interface FileTypeConfig {
  type: FileType
  extensions: string[]
  mimeTypes: string[]

  // Capabilities
  canView: boolean
  canEdit: boolean              // Can be edited in Yjs editor
  canQuery: boolean             // Can be queried with DuckDB
  canCollaborate: boolean       // Supports real-time collaboration

  // For AI agent
  agentAccess: 'none' | 'read' | 'write'
}

export const FILE_TYPE_CONFIGS: Record<FileType, FileTypeConfig> = {
  // --- Spreadsheets ---
  csv: {
    type: 'csv',
    extensions: ['.csv'],
    mimeTypes: ['text/csv'],
    canView: true, canEdit: true, canQuery: true, canCollaborate: true,
    agentAccess: 'write',
  },
  tsv: {
    type: 'tsv',
    extensions: ['.tsv'],
    mimeTypes: ['text/tab-separated-values'],
    canView: true, canEdit: true, canQuery: true, canCollaborate: true,
    agentAccess: 'write',
  },
  xlsx: {
    type: 'xlsx',
    extensions: ['.xlsx', '.xls'],
    mimeTypes: ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],
    canView: true, canEdit: true, canQuery: true, canCollaborate: true,
    agentAccess: 'write',
  },
  ods: {
    type: 'ods',
    extensions: ['.ods', '.fods', '.ots'],
    mimeTypes: ['application/vnd.oasis.opendocument.spreadsheet'],
    canView: true, canEdit: true, canQuery: false, canCollaborate: true,
    agentAccess: 'write',
  },
  parquet: {
    type: 'parquet',
    extensions: ['.parquet'],
    mimeTypes: ['application/octet-stream'],
    canView: true, canEdit: false, canQuery: true, canCollaborate: false,
    agentAccess: 'read',
  },

  // --- Documents ---
  docx: {
    type: 'docx',
    extensions: ['.docx'],
    mimeTypes: ['application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
    canView: true, canEdit: true, canQuery: false, canCollaborate: true,
    agentAccess: 'write',
  },
  doc: {
    type: 'doc',
    extensions: ['.doc'],
    mimeTypes: ['application/msword'],
    canView: true, canEdit: true, canQuery: false, canCollaborate: true,
    agentAccess: 'write',
  },
  odt: {
    type: 'odt',
    extensions: ['.odt', '.fodt', '.ott'],
    mimeTypes: ['application/vnd.oasis.opendocument.text'],
    canView: true, canEdit: true, canQuery: false, canCollaborate: true,
    agentAccess: 'write',
  },
  rtf: {
    type: 'rtf',
    extensions: ['.rtf'],
    mimeTypes: ['application/rtf'],
    canView: true, canEdit: true, canQuery: false, canCollaborate: true,
    agentAccess: 'write',
  },
  pdf: {
    type: 'pdf',
    extensions: ['.pdf'],
    mimeTypes: ['application/pdf'],
    canView: true, canEdit: false, canQuery: false, canCollaborate: false,
    agentAccess: 'read',
  },

  // --- Presentations ---
  pptx: {
    type: 'pptx',
    extensions: ['.pptx'],
    mimeTypes: ['application/vnd.openxmlformats-officedocument.presentationml.presentation'],
    canView: true, canEdit: true, canQuery: false, canCollaborate: true,
    agentAccess: 'write',
  },
  ppt: {
    type: 'ppt',
    extensions: ['.ppt'],
    mimeTypes: ['application/vnd.ms-powerpoint'],
    canView: true, canEdit: true, canQuery: false, canCollaborate: true,
    agentAccess: 'write',
  },
  odp: {
    type: 'odp',
    extensions: ['.odp', '.fodp', '.otp'],
    mimeTypes: ['application/vnd.oasis.opendocument.presentation'],
    canView: true, canEdit: true, canQuery: false, canCollaborate: true,
    agentAccess: 'write',
  },

  // --- Code / markup ---
  json: {
    type: 'json',
    extensions: ['.json'],
    mimeTypes: ['application/json'],
    canView: true, canEdit: true, canQuery: true, canCollaborate: true,
    agentAccess: 'write',
  },
  md: {
    type: 'md',
    extensions: ['.md', '.markdown'],
    mimeTypes: ['text/markdown'],
    canView: true, canEdit: true, canQuery: false, canCollaborate: true,
    agentAccess: 'write',
  },
  html: {
    type: 'html',
    extensions: ['.html', '.htm'],
    mimeTypes: ['text/html'],
    canView: true, canEdit: true, canQuery: false, canCollaborate: true,
    agentAccess: 'write',
  },
  xml: {
    type: 'xml',
    extensions: ['.xml'],
    mimeTypes: ['application/xml', 'text/xml'],
    canView: true, canEdit: false, canQuery: false, canCollaborate: false,
    agentAccess: 'read',
  },
  yaml: {
    type: 'yaml',
    extensions: ['.yaml', '.yml'],
    mimeTypes: ['application/x-yaml', 'text/yaml'],
    canView: true, canEdit: false, canQuery: false, canCollaborate: false,
    agentAccess: 'read',
  },

  // --- Text ---
  txt: {
    type: 'txt',
    extensions: ['.txt'],
    mimeTypes: ['text/plain'],
    canView: true, canEdit: true, canQuery: false, canCollaborate: true,
    agentAccess: 'write',
  },
  log: {
    type: 'log',
    extensions: ['.log'],
    mimeTypes: ['text/plain'],
    canView: true, canEdit: false, canQuery: false, canCollaborate: false,
    agentAccess: 'read',
  },
  ppn: {
    type: 'ppn',
    extensions: ['.ppn'],
    mimeTypes: ['application/json'],
    canView: true, canEdit: true, canQuery: false, canCollaborate: true,
    agentAccess: 'write',
  },

  // --- Images ---
  png: {
    type: 'png',
    extensions: ['.png'],
    mimeTypes: ['image/png'],
    canView: true, canEdit: false, canQuery: false, canCollaborate: false,
    agentAccess: 'read',
  },
  jpg: {
    type: 'jpg',
    extensions: ['.jpg', '.jpeg'],
    mimeTypes: ['image/jpeg'],
    canView: true, canEdit: false, canQuery: false, canCollaborate: false,
    agentAccess: 'read',
  },
  gif: {
    type: 'gif',
    extensions: ['.gif'],
    mimeTypes: ['image/gif'],
    canView: true, canEdit: false, canQuery: false, canCollaborate: false,
    agentAccess: 'read',
  },
  webp: {
    type: 'webp',
    extensions: ['.webp'],
    mimeTypes: ['image/webp'],
    canView: true, canEdit: false, canQuery: false, canCollaborate: false,
    agentAccess: 'read',
  },
  svg: {
    type: 'svg',
    extensions: ['.svg'],
    mimeTypes: ['image/svg+xml'],
    canView: true, canEdit: false, canQuery: false, canCollaborate: false,
    agentAccess: 'read',
  },

  // --- Video ---
  mp4: {
    type: 'mp4',
    extensions: ['.mp4'],
    mimeTypes: ['video/mp4'],
    canView: true, canEdit: false, canQuery: false, canCollaborate: false,
    agentAccess: 'none',
  },
  webm: {
    type: 'webm',
    extensions: ['.webm'],
    mimeTypes: ['video/webm'],
    canView: true, canEdit: false, canQuery: false, canCollaborate: false,
    agentAccess: 'none',
  },
  mov: {
    type: 'mov',
    extensions: ['.mov'],
    mimeTypes: ['video/quicktime'],
    canView: true, canEdit: false, canQuery: false, canCollaborate: false,
    agentAccess: 'none',
  },

  // --- Archive ---
  zip: {
    type: 'zip',
    extensions: ['.zip'],
    mimeTypes: ['application/zip'],
    canView: false, canEdit: false, canQuery: false, canCollaborate: false,
    agentAccess: 'none',
  },

  // --- Database ---
  db: {
    type: 'db',
    extensions: ['.db', '.sqlite', '.sqlite3'],
    mimeTypes: ['application/x-sqlite3', 'application/vnd.sqlite3'],
    canView: true, canEdit: false, canQuery: true, canCollaborate: false,
    agentAccess: 'read',
  },

  // --- Special ---
  sqlparz: {
    type: 'sqlparz',
    extensions: ['.sqlparz'],
    mimeTypes: ['application/json'],
    canView: true, canEdit: false, canQuery: true, canCollaborate: false,
    agentAccess: 'read',
  },
  folder: {
    type: 'folder',
    extensions: [],
    mimeTypes: [],
    canView: true, canEdit: false, canQuery: false, canCollaborate: false,
    agentAccess: 'write',
  },
  unknown: {
    type: 'unknown',
    extensions: [],
    mimeTypes: [],
    canView: false, canEdit: false, canQuery: false, canCollaborate: false,
    agentAccess: 'none',
  },
}

/**
 * Get file type from extension
 */
export function getFileType(filename: string): FileType {
  const ext = filename.toLowerCase().split('.').pop()
  if (!ext) return 'unknown'

  for (const [type, config] of Object.entries(FILE_TYPE_CONFIGS)) {
    if (config.extensions.includes(`.${ext}`)) {
      return type as FileType
    }
  }

  return 'unknown'
}

/**
 * #762 — the request header a client sends a per-action idempotency key in. The server keys a queued file job
 * (copy, extract, cross-org move) on it, so the same request sent twice joins the first job. Its own header, never
 * `x-operation-id`, which also turns on per-file progress events.
 */
export const IDEMPOTENCY_KEY_HEADER = 'x-idempotency-key'
